import { FC, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Autocomplete,
  Box,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from "@mui/material";
import {
  CommentDto,
  Country,
  CountryFinanceParty,
  CountryPartyType
} from "../../../../../../api/Api";
import { CommentsSection } from "../../../../../../components/lifecycle/commentsSection";
import { LifecycleDialog } from "../../../../../../components/lifecycle/lifecycleDialog";
import { SignOffNoteField } from "../../../../../../components/lifecycle/signOffNoteField";
import { useCountryAssignees } from "../../../../dataHooks/countryQueries";
import { countryAttachmentApi } from "../../../countryAttachmentApi";
import { useCountryLifecycle } from "../../useCountryLifecycle";
import { PartyRows } from "../../parties";
import {
  selectedPartyTypes,
  syncPartyRows,
  updatePartyRow
} from "../../parties/utils";

type Props = {
  specificCountry: Country;
};

type FormData = {
  testTrade: string;
  testTradeAssignee: string;
  callback: string;
  callbackAssignee: string;
  signOffNote: string;
  comments: CommentDto[];
};

const emptyFinanceParty = (
  partyType: CountryPartyType
): CountryFinanceParty => ({
  partyType,
  agreementReview: "",
  businessCentralSetup: "",
  iban: "",
  assignee: ""
});

/**
 * Agreement review, Business Central setup and IBAN are per party (board #69), so they sit on rows
 * rather than as three flat fields the way the broker Finance dialog has them.
 *
 * Rows cover every party Market Access marked needed. Whether that should narrow to the parties MFT
 * actually pays is still open with Market Access, see docs/country-additions-plan.md; until then
 * Finance dismisses the ones that do not apply with "not needed" on the row.
 */
export const Finance: FC<Props> = ({ specificCountry }) => {
  const params = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const lifecycle = specificCountry.financeLifecycle;
  const marketAccess = specificCountry.marketAccessLifecycle;

  const [formData, setFormData] = useState<FormData>({
    testTrade: lifecycle?.testTrade || "",
    testTradeAssignee: lifecycle?.testTradeAssignee || "",
    callback: lifecycle?.callback || "",
    callbackAssignee: lifecycle?.callbackAssignee || "",
    signOffNote: lifecycle?.signOffNote || "",
    comments: lifecycle?.comments || []
  });

  const [parties, setParties] = useState<CountryFinanceParty[]>(() =>
    syncPartyRows(
      lifecycle?.parties,
      selectedPartyTypes(marketAccess),
      emptyFinanceParty
    )
  );

  const handleClose = () => navigate(`/countries/country/${params.id}`);

  const extraSlice = () => ({ parties });

  const {
    handleSave,
    handleComplete,
    isSaving,
    progressOverride,
    currentComment,
    setCurrentComment,
    addComment,
    deleteComment
  } = useCountryLifecycle({
    country: specificCountry,
    lifecycle: "financeLifecycle",
    formData,
    setFormData,
    onClose: handleClose
  });

  const financeAssignees = useCountryAssignees("finance");

  const handleChange = (field: keyof FormData, value: string) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  const open = location.pathname.includes("finance");

  const canComplete = Boolean(
    parties.every(
      (party) => party.agreementReview && party.businessCentralSetup
    ) &&
      formData.testTrade &&
      formData.callback
  );

  return (
    <LifecycleDialog
      open={open}
      onClose={handleClose}
      title="Finance"
      progressOverride={progressOverride}
      maxWidth="md"
      attachmentApi={countryAttachmentApi}
      actions={{
        onClose: handleClose,
        onSave: () => handleSave(extraSlice()),
        onComplete: () => handleComplete(extraSlice()),
        canComplete,
        isSaving
      }}
    >
      <Box sx={{ paddingTop: 2 }} gap={2} display="flex" flexDirection="column">
        <PartyRows
          title="Per party setup"
          rows={parties}
          marketAccess={marketAccess}
          emptyMessage="Market Access has not marked any party as needed yet."
          renderRow={(party) => {
            const partyType = party.partyType as CountryPartyType;
            const update = (patch: Partial<CountryFinanceParty>) =>
              setParties((prev) => updatePartyRow(prev, partyType, patch));

            return (
              <>
                <Box display="flex" gap={2}>
                  <FormControl size="medium" required sx={{ flex: 1 }}>
                    <InputLabel>Agreement review</InputLabel>
                    <Select
                      label="Agreement review"
                      value={party.agreementReview || ""}
                      onChange={(e) =>
                        update({ agreementReview: e.target.value })
                      }
                    >
                      <MenuItem value="approved">Approved</MenuItem>
                      <MenuItem value="rejected">Rejected</MenuItem>
                      <MenuItem value="not_needed">Not needed</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl size="medium" required sx={{ flex: 1 }}>
                    <InputLabel>Setup in Business Central</InputLabel>
                    <Select
                      label="Setup in Business Central"
                      value={party.businessCentralSetup || ""}
                      onChange={(e) =>
                        update({ businessCentralSetup: e.target.value })
                      }
                    >
                      <MenuItem value="initiated">Initiated</MenuItem>
                      <MenuItem value="completed">Completed</MenuItem>
                      <MenuItem value="rejected">Rejected</MenuItem>
                      <MenuItem value="not_needed">Not needed</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <Box display="flex" gap={2}>
                  <TextField
                    sx={{ flex: 1 }}
                    label="IBAN"
                    value={party.iban || ""}
                    onChange={(e) => update({ iban: e.target.value })}
                  />

                  <Autocomplete
                    sx={{ flex: 1 }}
                    options={financeAssignees.data || []}
                    getOptionLabel={(option) =>
                      `${option.name} (${option.email})`
                    }
                    value={
                      financeAssignees.data?.find(
                        (a) => a.email === party.assignee
                      ) || null
                    }
                    onChange={(_, newValue) =>
                      update({ assignee: newValue?.email || "" })
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="Assignee" size="medium" />
                    )}
                  />
                </Box>
              </>
            );
          }}
        />

        <Divider />

        <Box display="flex" gap={2}>
          <FormControl size="medium" required sx={{ flex: 1 }}>
            <InputLabel>Test trade for settlement method</InputLabel>
            <Select
              label="Test trade for settlement method"
              value={formData.testTrade}
              onChange={(e) => handleChange("testTrade", e.target.value)}
            >
              <MenuItem value="needed">Needed</MenuItem>
              <MenuItem value="not_needed">Not needed</MenuItem>
            </Select>
          </FormControl>

          <Autocomplete
            sx={{ flex: 1 }}
            options={financeAssignees.data || []}
            getOptionLabel={(option) => `${option.name} (${option.email})`}
            value={
              financeAssignees.data?.find(
                (a) => a.email === formData.testTradeAssignee
              ) || null
            }
            onChange={(_, newValue) =>
              handleChange("testTradeAssignee", newValue?.email || "")
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Test trade assignee"
                size="medium"
              />
            )}
          />
        </Box>

        <Box display="flex" gap={2}>
          <FormControl size="medium" required sx={{ flex: 1 }}>
            <InputLabel>Callback</InputLabel>
            <Select
              label="Callback"
              value={formData.callback}
              onChange={(e) => handleChange("callback", e.target.value)}
            >
              <MenuItem value="initiated">Initiated</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="not_needed">Not needed</MenuItem>
            </Select>
          </FormControl>

          <Autocomplete
            sx={{ flex: 1 }}
            options={financeAssignees.data || []}
            getOptionLabel={(option) => `${option.name} (${option.email})`}
            value={
              financeAssignees.data?.find(
                (a) => a.email === formData.callbackAssignee
              ) || null
            }
            onChange={(_, newValue) =>
              handleChange("callbackAssignee", newValue?.email || "")
            }
            renderInput={(params) => (
              <TextField {...params} label="Callback assignee" size="medium" />
            )}
          />
        </Box>

        <SignOffNoteField
          value={formData.signOffNote}
          onChange={(value) => handleChange("signOffNote", value)}
        />

        <CommentsSection
          loading={isSaving}
          comments={formData.comments}
          currentComment={currentComment}
          onCurrentCommentChange={setCurrentComment}
          saveComment={addComment}
          onDeleteComment={deleteComment}
        />
      </Box>
    </LifecycleDialog>
  );
};
